import { useHabits as _useHabits } from '@griply/hooks';
import { Habit } from '@griply/types';
import {
  DeletedFilter,
  DeletedFilterOption,
  filterTasksByLinks,
  isClientDerivableTaskOptions,
  selectTasksByStatus,
} from '@griply/utils';
import { useMemo } from 'react';
import { GetTaskOptions } from 'shared/services/backend-api';
import { TaskType } from 'shared/types/task-base';

import { useAllHabits } from './use-all-habits';
import { useLegacyTasksOfType } from './use-legacy-tasks-of-type';

export type Options = Omit<GetTaskOptions, 'type'> & DeletedFilterOption;

export const useHabits = ({
  deletedFilter = DeletedFilter.ExcludeDeleted,
  ...options
}: Options = {}): Habit[] => {
  const derivable = isClientDerivableTaskOptions(options);
  const baseHabits = useAllHabits(deletedFilter);
  const legacyTasks = useLegacyTasksOfType(
    TaskType.Habit,
    options,
    deletedFilter,
    !derivable,
  );
  const legacyHabits = _useHabits({ tasks: legacyTasks });

  const {
    uncompletedOnly,
    completedOnly,
    unArchivedOnly,
    unFrozenOnly,
    archivedOnly,
    goals,
    lifeAreas,
  } = options;

  const derivedHabits = useMemo(
    () =>
      filterTasksByLinks(
        selectTasksByStatus(baseHabits, {
          uncompletedOnly,
          completedOnly,
          unArchivedOnly,
          unFrozenOnly,
          archivedOnly,
        }) ?? baseHabits,
        { goals, lifeAreas },
      ),
    [
      baseHabits,
      uncompletedOnly,
      completedOnly,
      unArchivedOnly,
      unFrozenOnly,
      archivedOnly,
      goals,
      lifeAreas,
    ],
  );

  return derivable ? derivedHabits : legacyHabits;
};
